import React, { useMemo } from "react";
import { mulberry32 } from "./textures";
import Bird from "./Bird";
import Critter from "./Critter";
import Horse from "./Horse";
import Gazelle from "./Gazelle";

interface WildlifeProps {
  avoidPositions: [number, number, number][];
}

const WORLD_RADIUS = 36;
const CLEAR_RADIUS = 6;
const AVOID_CLEARANCE = 6; // generous enough that horses/gazelles wandering don't drift into caves/markers

const CRITTER_COLORS = ["#b8895a", "#8a8a8a", "#6b4a30", "#d4c4a0"];

const Wildlife: React.FC<WildlifeProps> = ({ avoidPositions }) => {
  const { birds, critters, horses, gazelles } = useMemo(() => {
    const rand = mulberry32(77);

    const isClear = (x: number, z: number) => {
      if (Math.hypot(x, z) < CLEAR_RADIUS) return false;
      return !avoidPositions.some(
        ([ax, , az]) => Math.hypot(x - ax, z - az) < AVOID_CLEARANCE,
      );
    };

    const birds: { center: [number, number, number]; radius: number; height: number; speed: number; phase: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = rand() * Math.PI * 2;
      const dist = 8 + rand() * (WORLD_RADIUS - 12);
      birds.push({
        center: [Math.cos(angle) * dist, 0, Math.sin(angle) * dist],
        radius: 3 + rand() * 4,
        height: 6 + rand() * 5,
        speed: 0.25 + rand() * 0.25,
        phase: rand() * Math.PI * 2,
      });
    }

    const critters: { center: [number, number, number]; radius: number; speed: number; phase: number; color: string }[] = [];
    let attempts = 0;
    while (critters.length < 9 && attempts < 200) {
      attempts++;
      const x = (rand() - 0.5) * WORLD_RADIUS * 2;
      const z = (rand() - 0.5) * WORLD_RADIUS * 2;
      if (Math.hypot(x, z) > WORLD_RADIUS - 3) continue;
      if (!isClear(x, z)) continue;
      critters.push({
        center: [x, 0, z],
        radius: 1 + rand() * 1.5,
        speed: 0.3 + rand() * 0.4,
        phase: rand() * Math.PI * 2,
        color: CRITTER_COLORS[Math.floor(rand() * CRITTER_COLORS.length)],
      });
    }

    const horses: { position: [number, number, number]; rotationY: number }[] = [];
    attempts = 0;
    while (horses.length < 4 && attempts < 150) {
      attempts++;
      const x = (rand() - 0.5) * WORLD_RADIUS * 2;
      const z = (rand() - 0.5) * WORLD_RADIUS * 2;
      if (Math.hypot(x, z) > WORLD_RADIUS - 4) continue;
      if (!isClear(x, z)) continue;
      horses.push({ position: [x, 0, z], rotationY: rand() * Math.PI * 2 });
    }

    const gazelles: { position: [number, number, number]; rotationY: number }[] = [];
    attempts = 0;
    while (gazelles.length < 5 && attempts < 150) {
      attempts++;
      const x = (rand() - 0.5) * WORLD_RADIUS * 2;
      const z = (rand() - 0.5) * WORLD_RADIUS * 2;
      if (Math.hypot(x, z) > WORLD_RADIUS - 4) continue;
      if (!isClear(x, z)) continue;
      gazelles.push({ position: [x, 0, z], rotationY: rand() * Math.PI * 2 });
    }

    return { birds, critters, horses, gazelles };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <group>
      {birds.map((b, i) => (
        <Bird key={`bird-${i}`} {...b} />
      ))}
      {critters.map((c, i) => (
        <Critter key={`critter-${i}`} {...c} />
      ))}
      {horses.map((h, i) => (
        <Horse key={`horse-${i}`} {...h} />
      ))}
      {gazelles.map((g, i) => (
        <Gazelle key={`gazelle-${i}`} {...g} />
      ))}
    </group>
  );
};

export default Wildlife;
